import { useEffect, useRef, useState } from 'react';

const HIDE_DELAY_MS = 7000;

/**
 * Manages the visibility of a cart sheet panel with a timed auto-hide.
 *
 * When `shouldBeHidden` transitions to true, the sheet is shown until
 * HIDE_DELAY_MS elapses, then hidden. If `shouldBeHidden` reverts to false
 * before the delay completes, the timeout is cancelled and the sheet is
 * shown immediately.
 *
 * The sheet starts hidden (true) so it only appears once a product is added.
 *
 * @param {boolean} shouldBeHidden - Whether the sheet should eventually be hidden.
 * @returns {boolean} Whether the sheet is currently hidden.
 */
export function useHideCartSheet(shouldBeHidden) {
    const [hidden, setHidden] = useState(true);
    const timeoutRef = useRef(undefined);

    useEffect(() => {
        if (!shouldBeHidden) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
            setHidden(false);
        } else if (!hidden && timeoutRef.current === undefined) {
            timeoutRef.current = setTimeout(() => {
                setHidden(true);
                timeoutRef.current = undefined;
            }, HIDE_DELAY_MS);
        }

        return () => clearTimeout(timeoutRef.current);
    }, [shouldBeHidden, hidden]);

    return hidden;
}
